import React from "react";
import { Button } from "primereact/button";
import { Message } from "primereact/message";

import { UiHealth } from "../../../types/eroundProvisioning";
import { unreachableReason } from "../../../utils/eroundProvisioning";
import { formatInstant } from "../../../utils/instantFormat";

// Why a check could not reach the screens, from the check's outcome.
const OUTCOME: Record<string, string> = {
  login_failed: "signing in to eRound failed",
  timeout: "eRound did not respond in time",
  canary_missing: "the test station in eRound is missing",
};

type Props = { health: UiHealth | null | undefined; onOpen: () => void };

/**
 * Warns above the tabs while an eRound screen the automation uses has changed or could not be
 * checked (SOPS-164). Healthy, never checked and a failed load show nothing: the tab carries those.
 */
const UiHealthBanner: React.FC<Props> = ({ health, onOpen }) => {
  const screens = health?.screens ?? [];
  const drifted = screens.filter((s) => s.status === "drift");
  const affected = drifted.length ? drifted : screens.filter((s) => s.status === "unreachable");
  if (!health || !affected.length) return null;

  const [first] = affected;
  const more = affected.length > 1 ? `, and ${affected.length - 1} more` : "";
  const reason = OUTCOME[health.outcome ?? ""] ?? unreachableReason(first.reason);

  return (
    <Message
      severity="warn"
      className="w-full justify-content-start mb-3"
      content={
        <div>
          {drifted.length ? (
            <>
              <strong>eRound changed a screen the automation uses.</strong> <em>{first.title}</em> since{" "}
              {formatInstant(first.since)}
              {more}. Runs that reach it stop before saving, until the automation is updated.
            </>
          ) : (
            <>
              <strong>The nightly eRound screen check could not run: {reason}.</strong>{" "}
              <em>{first.title}</em> not checked since {formatInstant(first.since)}
              {more}.
            </>
          )}{" "}
          <Button link label="See screen health" className="p-0" onClick={onOpen} />
        </div>
      }
    />
  );
};

export default UiHealthBanner;
